import React, { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { ListChecks, Download, Loader2, Upload, X } from 'lucide-react';
import { getParisToday } from '@/components/CalendarWidget';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Formate l'heure d'un événement en heure de Paris, quel que soit le fuseau
// horaire de l'appareil (même logique que sur la page Main Courante).
const formatEventTime = (isoString) => {
  if (!isoString) return '';
  return new Intl.DateTimeFormat('fr-FR', {
    timeZone: 'Europe/Paris', hour: '2-digit', minute: '2-digit', hour12: false
  }).format(new Date(isoString));
};

// Génère, à partir d'une sélection libre d'événements de la journée choisie
// par le chef de poste, un rapport PDF dans la même mise en page que le
// rapport de main courante quotidien (voir generate_daily_pdf_report côté
// serveur, réutilisé pour ce rapport via un titre "RAPPORT DE RONDE").
//
// Flux en deux temps : "Importer des événements" ouvre une popup listant les
// événements du site/de la date choisis à cocher ; une fois validée, la
// sélection retenue s'affiche dans la carte principale (import éditable
// tant que le rapport n'a pas été généré).
const RondeReportPDF = ({ sites }) => {
  const [selectedSite, setSelectedSite] = useState('');
  const [selectedDate, setSelectedDate] = useState(getParisToday());
  const [importedEvents, setImportedEvents] = useState([]);
  const [generating, setGenerating] = useState(false);

  const [showImportDialog, setShowImportDialog] = useState(false);
  const [dialogEvents, setDialogEvents] = useState([]);
  const [dialogSelectedIds, setDialogSelectedIds] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);

  const openImportDialog = async () => {
    if (!selectedSite) {
      toast.warning('Veuillez sélectionner un site');
      return;
    }
    setShowImportDialog(true);
    setLoadingEvents(true);
    // Reprend la sélection déjà importée si elle correspond au même site/date,
    // pour permettre de revenir modifier son choix sans repartir de zéro.
    setDialogSelectedIds(importedEvents.map((e) => e.id));
    const token = localStorage.getItem('token');
    try {
      const params = new URLSearchParams({
        site_id: selectedSite,
        start_date: `${selectedDate}T00:00:00`,
        end_date: `${selectedDate}T23:59:59`
      });
      const res = await fetch(`${BACKEND_URL}/api/events?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setDialogEvents(await res.json());
      } else {
        toast.error('Erreur lors du chargement des événements');
      }
    } catch {
      toast.error('Erreur de connexion');
    } finally {
      setLoadingEvents(false);
    }
  };

  const toggleDialogEvent = (eventId, checked) => {
    setDialogSelectedIds((prev) => checked ? [...prev, eventId] : prev.filter((id) => id !== eventId));
  };

  const toggleDialogAll = (checked) => {
    setDialogSelectedIds(checked ? dialogEvents.map((e) => e.id) : []);
  };

  const handleValidateImport = () => {
    setImportedEvents(dialogEvents.filter((e) => dialogSelectedIds.includes(e.id)));
    setShowImportDialog(false);
  };

  const removeImportedEvent = (eventId) => {
    setImportedEvents((prev) => prev.filter((e) => e.id !== eventId));
  };

  const handleGenerate = async () => {
    if (!selectedSite) {
      toast.warning('Veuillez sélectionner un site');
      return;
    }
    if (importedEvents.length === 0) {
      toast.warning('Importez au moins un événement');
      return;
    }

    setGenerating(true);
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${BACKEND_URL}/api/rondes-report/pdf`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ site_id: selectedSite, event_ids: importedEvents.map((e) => e.id) })
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `rapport_ronde_${selectedDate}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success('Rapport de ronde téléchargé');
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erreur lors de la génération');
      }
    } catch {
      toast.error('Erreur de connexion');
    } finally {
      setGenerating(false);
    }
  };

  const dialogAllChecked = dialogEvents.length > 0 && dialogSelectedIds.length === dialogEvents.length;

  return (
    <Card className="glass-effect">
      <CardHeader>
        <div className="flex items-center gap-2">
          <ListChecks className="h-5 w-5 text-indigo-600" />
          <CardTitle>Rapport de ronde</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-gray-500 dark:text-slate-400">
          Importez les événements de la journée à inclure : le rapport PDF généré reprend la même
          mise en page que le rapport de main courante quotidien.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Site</Label>
            <Select value={selectedSite} onValueChange={(v) => { setSelectedSite(v); setImportedEvents([]); }}>
              <SelectTrigger data-testid="ronde-site-select">
                <SelectValue placeholder="Sélectionner un site" />
              </SelectTrigger>
              <SelectContent>
                {sites.map((site) => (
                  <SelectItem key={site.id} value={site.id}>{site.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Date</Label>
            <Input
              type="date"
              value={selectedDate}
              onChange={(e) => { setSelectedDate(e.target.value); setImportedEvents([]); }}
              max={getParisToday()}
              data-testid="ronde-date-input"
            />
          </div>
        </div>

        <Button
          type="button"
          variant="outline"
          className="w-full"
          onClick={openImportDialog}
          disabled={!selectedSite}
          data-testid="ronde-import-btn"
        >
          <Upload className="h-4 w-4 mr-2" />
          Importer des événements
        </Button>

        {importedEvents.length > 0 && (
          <div className="space-y-2">
            <Label>Événements importés ({importedEvents.length})</Label>
            <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
              {importedEvents.map((event) => (
                <div
                  key={event.id}
                  className="flex items-start gap-2 p-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800"
                  data-testid={`ronde-imported-${event.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-gray-500 dark:text-slate-400">
                      {formatEventTime(event.event_datetime)} — {event.category} / {event.event_type}
                    </p>
                    <p className="text-sm text-gray-700 dark:text-slate-200 truncate">{event.description}</p>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 shrink-0"
                    onClick={() => removeImportedEvent(event.id)}
                    data-testid={`ronde-remove-${event.id}`}
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        <Button
          onClick={handleGenerate}
          className="btn-primary w-full"
          disabled={generating || !selectedSite || importedEvents.length === 0}
          data-testid="ronde-generate-btn"
        >
          {generating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
          Générer le rapport ({importedEvents.length} événement{importedEvents.length !== 1 ? 's' : ''})
        </Button>
      </CardContent>

      {/* Popup d'import : liste des événements du jour choisi, à cocher */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Importer des événements — {selectedDate.split('-').reverse().join('/')}</DialogTitle>
          </DialogHeader>

          {dialogEvents.length > 0 && (
            <div className="flex items-center gap-2 pb-2 border-b border-slate-200 dark:border-slate-700">
              <Checkbox id="ronde-select-all" checked={dialogAllChecked} onCheckedChange={toggleDialogAll} />
              <label htmlFor="ronde-select-all" className="text-sm text-gray-600 dark:text-slate-300 cursor-pointer">
                Tout sélectionner
              </label>
            </div>
          )}

          {loadingEvents ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
            </div>
          ) : dialogEvents.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-slate-400 text-center py-6">
              Aucun événement pour cette date
            </p>
          ) : (
            <div className="space-y-1.5">
              {dialogEvents.map((event) => {
                const checked = dialogSelectedIds.includes(event.id);
                return (
                  <div
                    key={event.id}
                    className="flex items-start gap-2 p-2 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800"
                    data-testid={`ronde-event-${event.id}`}
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={(v) => toggleDialogEvent(event.id, !!v)}
                      className="mt-0.5"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-gray-500 dark:text-slate-400">
                        {formatEventTime(event.event_datetime)} — {event.category} / {event.event_type}
                      </p>
                      <p className="text-sm text-gray-700 dark:text-slate-200 truncate">{event.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setShowImportDialog(false)}>
              Annuler
            </Button>
            <Button
              type="button"
              className="btn-primary"
              onClick={handleValidateImport}
              disabled={dialogSelectedIds.length === 0}
              data-testid="ronde-import-validate-btn"
            >
              Valider ({dialogSelectedIds.length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default RondeReportPDF;
